import React from 'react';
import cx from 'classnames';
import { Card, Radio, Tooltip, Typography } from 'antd';
import {
  baseColor900,
  secondaryColor,
  secondaryColor50,
  baseColor400,
  colorTextDescription,
} from '@seaters/ui';
import { useTranslation } from 'react-i18next';
import styles from './RadioCard.module.css';

const { Text } = Typography;

export interface RadioCardOption {
  label: string;
  description?: string;
  value: string | number;
}

export interface RadioCardProps {
  option: RadioCardOption;
  isSelected: boolean;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
  testId?: string;
  /** Tooltip shown when the card is disabled, explaining why it can't be selected. */
  disabledTooltip?: string;
  /**
   * Pre-rendered description that bypasses translation. Use when the text is
   * dynamic (e.g. interpolated user data that must not be run through t()).
   */
  descriptionNode?: React.ReactNode;
}

export const RadioCard: React.FC<RadioCardProps> = ({
  option,
  isSelected,
  onClick,
  className,
  disabled = false,
  testId,
  disabledTooltip,
  descriptionNode,
}) => {
  const { t } = useTranslation();

  const card = (
    <Card
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled}
      styles={{ body: { padding: 16 } }}
      className={cx(
        'h-full min-h-[120px] transition-all duration-300',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        styles.radioCard,
        {
          [styles.radioCardSelected]: isSelected,
        },
        className
      )}
      data-testid={testId}
      style={
        {
          '--radio-card-border-color': baseColor400,
          '--radio-card-hover-border-color': disabled
            ? baseColor400
            : secondaryColor,
          '--radio-card-selected-bg': secondaryColor50,
          '--radio-card-selected-border': secondaryColor,
        } as React.CSSProperties
      }
    >
      <Radio value={option.value} disabled={disabled} checked={isSelected}>
        <Text
          strong={isSelected}
          style={{
            color: isSelected ? secondaryColor : baseColor900,
          }}
        >
          {t(option.label)}
        </Text>
      </Radio>
      <Text
        style={{
          color: colorTextDescription,
          display: 'block',
          marginTop: 8,
        }}
      >
        {descriptionNode ?? (option.description ? t(option.description) : null)}
      </Text>
    </Card>
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip title={disabledTooltip}>
        {/* span wrapper lets the tooltip work over a disabled element */}
        <span className="block h-full">{card}</span>
      </Tooltip>
    );
  }

  return card;
};
